import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  DatePicker,
  Button,
  Table,
  Card,
  Space,
  Modal,
  message,
  Typography,
  Row,
  Col,
  AutoComplete,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Resizable, ResizeCallbackData } from "react-resizable";
import "react-resizable/css/styles.css";
import { ReviewReportsService } from "../services/review-reports.service";
import ImageUpload from "../components/ImageUpload";
import type {
  VisitReportForReview,
  ReviewReportsQuery,
} from "../types/review-reports.types";
import { VisitReportState } from "../types/review-reports.types";
import dayjs, { Dayjs } from "dayjs";

const { RangePicker } = DatePicker;
const { Text } = Typography;

// 状态评估项目选项
const ASSESSMENT_ITEM_OPTIONS = [
  { value: "身体状态", label: "身体状态" },
  { value: "精神状态", label: "精神状态" },
  { value: "饮食状态", label: "饮食状态" },
  { value: "财务安全", label: "财务安全" },
  { value: "环境安全", label: "环境安全" },
];

// 可调整列宽的表头组件
interface ResizableHeaderProps {
  width: number;
  onResize: (width: number) => void;
  children: React.ReactNode;
  [key: string]: any;
}

const ResizableHeader: React.FC<ResizableHeaderProps> = ({
  width,
  onResize,
  children,
  ...restProps
}) => {
  if (!width) {
    return <th {...restProps}>{children}</th>;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      }
      onResize={(_: React.SyntheticEvent, { size }: ResizeCallbackData) => {
        onResize(size.width);
      }}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps}>{children}</th>
    </Resizable>
  );
};

// 默认列宽配置
const DEFAULT_COLUMN_WIDTHS = {
  id: 100,
  staffName: 100,
  customerName: 100,
  customerPhone: 140,
  elderInfo: 250,
  elderPhone: 140,
  elderAddress: 200,
  startAt: 150,
  endAt: 150,
  durationMinutes: 120,
  chatRecordUrl: 150,
  createdAt: 150,
  action: 120,
};

// 性别转换函数
const formatGender = (gender: "男" | "女" | "未知"): string => {
  return gender;
};

interface SearchParams {
  dateRange?: [Dayjs, Dayjs];
  customerName?: string;
  elderName?: string;
  staffName?: string;
  state?: VisitReportState;
}

const PendingReports: React.FC = () => {
  const [form] = Form.useForm<SearchParams>();
  const [loading, setLoading] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<VisitReportForReview[]>([]);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState<SearchParams>({});
  const [chatModalVisible, setChatModalVisible] = useState<boolean>(false);
  const [auditModalVisible, setAuditModalVisible] = useState<boolean>(false);
  const [selectedReport, setSelectedReport] =
    useState<VisitReportForReview | null>(null);
  const [editableReport, setEditableReport] =
    useState<VisitReportForReview | null>(null);

  // 反馈图片上传管理
  const [feedbackImages, setFeedbackImages] = useState<string[]>([]);

  // 列宽度状态管理
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(
    DEFAULT_COLUMN_WIDTHS
  );

  // 处理列宽度调整
  const handleResize = (columnKey: string) => (width: number) => {
    setColumnWidths((prev) => ({
      ...prev,
      [columnKey]: width,
    }));
  };

  // 初始化数据
  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 构建查询参数的私有函数
  const buildQueryParams = (
    searchValues: SearchParams = {},
    page?: number,
    limit?: number
  ): ReviewReportsQuery => {
    const queryParams: ReviewReportsQuery = {
      page: page || pagination.current || 1,
      limit: limit || pagination.pageSize || 20,
      customerName: searchValues.customerName,
      elderName: searchValues.elderName,
      staffName: searchValues.staffName,
      state: VisitReportState.PENDING_REVIEW,
    };

    // 处理日期范围
    if (searchValues.dateRange && searchValues.dateRange.length === 2) {
      queryParams.startDate = searchValues.dateRange[0].format("YYYY-MM-DD");
      queryParams.endDate = searchValues.dateRange[1].format("YYYY-MM-DD");
    }

    return queryParams;
  };

  // 搜索功能
  const handleSearch = async (values: SearchParams = {}) => {
    setLoading(true);
    setSearchParams(values);

    try {
      // 构建查询参数
      const queryParams = buildQueryParams(values);

      // 调用API
      const response = await ReviewReportsService.getReviewReports(queryParams);

      if (response.success) {
        setDataSource(response.data.items);
        setPagination((prev) => ({
          ...prev,
          total: response.data.total,
          current: response.data.page,
          pageSize: response.data.limit,
        }));
      } else {
        message.error("获取数据失败");
      }
    } catch (error) {
      console.error("搜索失败:", error);
      message.error("搜索失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  };

  // 重置搜索
  const handleReset = () => {
    form.resetFields();
    setPagination((prev) => ({ ...prev, current: 1 }));
    handleSearch();
  };

  // 分页变化
  const handleTableChange = (newPagination: TablePaginationConfig) => {
    setPagination(newPagination);
    // 重新搜索当前页数据
    const queryParams = buildQueryParams(
      searchParams,
      newPagination.current || 1,
      newPagination.pageSize || 20
    );

    ReviewReportsService.getReviewReports(queryParams)
      .then((response) => {
        if (response.success) {
          setDataSource(response.data.items);
          setPagination({
            ...newPagination,
            total: response.data.total,
          });
        }
      })
      .catch((error) => {
        console.error("分页查询失败:", error);
        message.error("获取数据失败");
      });
  };

  // 显示报告审核模态框
  const showAuditModal = (record: VisitReportForReview) => {
    setSelectedReport(record);
    setEditableReport({ ...record });
    // 初始化反馈图片状态
    setFeedbackImages(record.feedback?.attachments || []);
    setAuditModalVisible(true);
  };

  // 处理报告内容编辑
  const handleReportEdit = (
    field: keyof VisitReportForReview,
    value: string
  ) => {
    setEditableReport((prev) =>
      prev
        ? {
            ...prev,
            [field]: value,
          }
        : null
    );
  };

  // 处理嵌套字段编辑
  const handleNestedEdit = (
    parentField: "aiSummary" | "feedback" | "overallConclusion",
    childField: string,
    value: string | string[] | number
  ) => {
    setEditableReport((prev) =>
      prev
        ? {
            ...prev,
            [parentField]: {
              ...prev[parentField],
              [childField]: value,
            },
          }
        : null
    );
  };

  // 处理反馈图片上传
  const handleFeedbackImageChange = (
    index: number,
    url: string | undefined
  ) => {
    setFeedbackImages((prev) => {
      const newImages = [...prev];
      if (url) {
        newImages[index] = url;
      } else {
        newImages.splice(index, 1);
      }
      return newImages;
    });
  };

  // 添加新的反馈图片
  const handleAddFeedbackImage = () => {
    setFeedbackImages((prev) => [...prev, ""]);
  };

  // 处理状态评估记录编辑
  const handleConclusionRecordEdit = (
    index: number,
    field: "item" | "content" | "status" | "risks",
    value: string | number | string[]
  ) => {
    setEditableReport((prev) => {
      if (!prev) return null;

      const newRecords = [...(prev.conclusions || [])];
      newRecords[index] = {
        ...newRecords[index],
        [field]: value,
      };

      return {
        ...prev,
        conclusions: newRecords,
      };
    });
  };

  // 添加新的状态评估项目
  const handleAddConclusionRecord = () => {
    setEditableReport((prev) => {
      if (!prev) return null;

      const newRecord = {
        item: "",
        content: "",
        status: 3,
        risks: [],
      };

      return {
        ...prev,
        conclusions: [...(prev.conclusions || []), newRecord],
      };
    });
  };

  // 删除状态评估项目
  const handleDeleteConclusionRecord = (index: number) => {
    const currentItem = editableReport?.conclusions?.[index];
    const itemName = currentItem?.item || `项目 ${index + 1}`;

    Modal.confirm({
      title: "确认删除",
      content: `确定要删除状态评估项目"${itemName}"吗？删除后将无法恢复。`,
      okText: "确定删除",
      okType: "danger",
      cancelText: "取消",
      onOk: () => {
        setEditableReport((prev) => {
          if (!prev || !prev.conclusions) return null;

          const newRecords = [...prev.conclusions];
          newRecords.splice(index, 1);

          return {
            ...prev,
            conclusions: newRecords,
          };
        });

        message.success(`已删除状态评估项目"${itemName}"`);
      },
    });
  };

  // 审核通过
  const handleApprove = async () => {
    if (!editableReport) return;

    // 验证AI总结字段长度
    const contentSummaryLength =
      editableReport.aiSummary?.contentSummary?.length || 0;
    const actionAdviceLength =
      editableReport.aiSummary?.actionAdvice?.length || 0;

    if (contentSummaryLength > 255) {
      message.error(`内容概要超出长度限制（${contentSummaryLength}/255字符）`);
      return;
    }

    if (actionAdviceLength > 255) {
      message.error(`行动建议超出长度限制（${actionAdviceLength}/255字符）`);
      return;
    }

    Modal.confirm({
      title: "确认审核通过",
      content: "确定要审核通过这份报告吗？修改的内容将同时保存。",
      onOk: async () => {
        try {
          // 准备提交数据，移除 overallConclusion 中的 statusName
          const submitData: any = {
            requirement: editableReport.requirement,
            feedback: {
              ...editableReport.feedback,
              attachments: feedbackImages, // 使用新的图片列表
            },
            aiSummary: editableReport.aiSummary,
            conclusions: editableReport.conclusions,
          };

          // 如果有总体结论，添加时排除 statusName
          if (editableReport.overallConclusion) {
            submitData.overallConclusion = {
              status: editableReport.overallConclusion.status,
              comment: editableReport.overallConclusion.comment,
              risks: editableReport.overallConclusion.risks,
            };
          }

          // 一次性调用审核通过API，同时保存修改内容
          await ReviewReportsService.approveReport(
            editableReport.id,
            submitData
          );

          message.success("报告审核通过，已发布");
          setAuditModalVisible(false);

          // 从列表中移除已审核的报告
          setDataSource((prev) =>
            prev.filter((item) => item.id !== editableReport.id)
          );

          // 更新分页信息
          setPagination((prev) => ({
            ...prev,
            total: prev.total! - 1,
          }));

          // 清理状态
          setEditableReport(null);
          setSelectedReport(null);
          setFeedbackImages([]);
        } catch (error) {
          console.error("审核通过失败:", error);
          message.error("审核通过失败，请稍后重试");
        }
      },
    });
  };

  // 审核拒绝
  const handleCancel = async () => {
    if (!selectedReport) return;

    setAuditModalVisible(false);
    setEditableReport(null);
    setSelectedReport(null);
    setFeedbackImages([]);
  };

  const columns: ColumnsType<VisitReportForReview> = [
    {
      title: "报告ID",
      dataIndex: "id",
      key: "id",
      width: columnWidths.id,
      onHeaderCell: () => ({
        width: columnWidths.id,
        onResize: handleResize("id"),
      }),
    },
    {
      title: "服务人员",
      dataIndex: "staffName",
      key: "staffName",
      width: columnWidths.staffName,
      onHeaderCell: () => ({
        width: columnWidths.staffName,
        onResize: handleResize("staffName"),
      }),
    },
    {
      title: "客户姓名",
      dataIndex: "customerName",
      key: "customerName",
      width: columnWidths.customerName,
      onHeaderCell: () => ({
        width: columnWidths.customerName,
        onResize: handleResize("customerName"),
      }),
    },
    {
      title: "客户手机号",
      dataIndex: "customerPhone",
      key: "customerPhone",
      width: columnWidths.customerPhone,
      onHeaderCell: () => ({
        width: columnWidths.customerPhone,
        onResize: handleResize("customerPhone"),
      }),
    },
    {
      title: "老人信息",
      key: "elderInfo",
      width: columnWidths.elderInfo,
      onHeaderCell: () => ({
        width: columnWidths.elderInfo,
        onResize: handleResize("elderInfo"),
      }),
      render: (_, record) => {
        return record.elderInfo.map((elder, index) => (
          <div
            key={index}
            style={{
              marginBottom: index < record.elderInfo.length - 1 ? "8px" : "0",
            }}
          >
            {`${elder.name}，${formatGender(elder.gender)}，${elder.age}岁，${
              elder.identityName
            }`}
          </div>
        ));
      },
    },
    {
      title: "老人手机号",
      dataIndex: "elderPhone",
      key: "elderPhone",
      width: columnWidths.elderPhone,
      onHeaderCell: () => ({
        width: columnWidths.elderPhone,
        onResize: handleResize("elderPhone"),
      }),
    },
    {
      title: "老人住址",
      dataIndex: "elderAddress",
      key: "elderAddress",
      width: columnWidths.elderAddress,
      ellipsis: true,
      onHeaderCell: () => ({
        width: columnWidths.elderAddress,
        onResize: handleResize("elderAddress"),
      }),
    },
    {
      title: "随访开始时间",
      dataIndex: "startAt",
      key: "startAt",
      width: columnWidths.startAt,
      onHeaderCell: () => ({
        width: columnWidths.startAt,
        onResize: handleResize("startAt"),
      }),
      render: (time?: string) =>
        time ? dayjs(time).format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      title: "随访结束时间",
      dataIndex: "endAt",
      key: "endAt",
      width: columnWidths.endAt,
      onHeaderCell: () => ({
        width: columnWidths.endAt,
        onResize: handleResize("endAt"),
      }),
      render: (time?: string) =>
        time ? dayjs(time).format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      title: "随访时长(分钟)",
      dataIndex: "durationMinutes",
      key: "durationMinutes",
      width: columnWidths.durationMinutes,
      onHeaderCell: () => ({
        width: columnWidths.durationMinutes,
        onResize: handleResize("durationMinutes"),
      }),
      render: (duration?: number) => (duration ? `${duration}分钟` : "-"),
    },
    {
      title: "原始聊天记录",
      key: "chatRecordUrl",
      width: columnWidths.chatRecordUrl,
      onHeaderCell: () => ({
        width: columnWidths.chatRecordUrl,
        onResize: handleResize("chatRecordUrl"),
      }),
      render: (_, record) =>
        record.chatRecordUrl ? (
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => window.open(record.chatRecordUrl, "_blank")}
          >
            查看聊天
          </Button>
        ) : (
          <span>-</span>
        ),
    },
    {
      title: "报告生成时间",
      dataIndex: "createdAt",
      key: "createdAt",
      width: columnWidths.createdAt,
      onHeaderCell: () => ({
        width: columnWidths.createdAt,
        onResize: handleResize("createdAt"),
      }),
      render: (time: string) => dayjs(time).format("YYYY-MM-DD HH:mm"),
    },
    {
      title: "操作",
      key: "action",
      width: columnWidths.action,
      fixed: "right",
      onHeaderCell: () => ({
        width: columnWidths.action,
        onResize: handleResize("action"),
      }),
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => showAuditModal(record)}
        >
          审核报告
        </Button>
      ),
    },
  ];

  return (
    <div>
      {/* 搜索区域 */}
      <Card style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline" onFinish={handleSearch}>
          <Row gutter={16} style={{ width: "100%" }}>
            <Col span={8}>
              <Form.Item name="dateRange" label="随访日期范围">
                <RangePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="customerName" label="客户姓名">
                <Input placeholder="请输入客户姓名" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="elderName" label="老人姓名">
                <Input placeholder="请输入老人姓名" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="staffName" label="服务人员">
                <Input placeholder="请输入服务人员姓名" />
              </Form.Item>
            </Col>
          </Row>
          <Row style={{ marginTop: 16 }}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                htmlType="submit"
                loading={loading}
              >
                搜索
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                重置
              </Button>
            </Space>
          </Row>
        </Form>
      </Card>

      {/* 列表区域 */}
      <Card>
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1600 }}
          components={{
            header: {
              cell: ResizableHeader,
            },
          }}
        />
      </Card>

      {/* 聊天记录模态框 */}
      <Modal
        title="聊天记录"
        open={chatModalVisible}
        onCancel={() => setChatModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedReport && (
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            {selectedReport.chatRecordUrl ? (
              <div>
                <p>
                  聊天记录URL:
                  <Button
                    type="link"
                    onClick={() =>
                      window.open(selectedReport.chatRecordUrl, "_blank")
                    }
                  >
                    点击查看
                  </Button>
                </p>
              </div>
            ) : (
              <div style={{ textAlign: "center", color: "#999" }}>
                暂无聊天记录
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 报告审核模态框 */}
      <Modal
        title={`报告审核【ID:${editableReport?.id || "未知"}】`}
        open={auditModalVisible}
        onCancel={() => setAuditModalVisible(false)}
        footer={[
          <Button
            key="approve"
            type="primary"
            icon={<CheckOutlined />}
            onClick={handleApprove}
          >
            审核通过
          </Button>,
          <Button
            key="reject"
            danger
            icon={<CloseOutlined />}
            onClick={handleCancel}
          >
            取消审核
          </Button>,
        ]}
        width={1000}
        style={{ top: 20 }}
      >
        {editableReport && (
          <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {/* 客户概要 */}
            <Card title="客户概要" size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Text strong>客户姓名：</Text>
                  {editableReport.customerName}
                </Col>
                <Col span={12}>
                  <Text strong>客户手机号：</Text>
                  {editableReport.customerPhone}
                </Col>
              </Row>
            </Card>

            {/* 老人概要 */}
            <Card title="老人概要" size="small" style={{ marginBottom: 16 }}>
              {editableReport.elderInfo.map((elder, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom:
                      index < editableReport.elderInfo.length - 1
                        ? "16px"
                        : "0",
                  }}
                >
                  <Row gutter={16}>
                    <Col span={6}>
                      <Text strong>老人姓名：</Text>
                      {elder.name}
                    </Col>
                    <Col span={6}>
                      <Text strong>性别：</Text>
                      {elder.gender}
                    </Col>
                    <Col span={6}>
                      <Text strong>年龄：</Text>
                      {elder.age}岁
                    </Col>
                    <Col span={6}>
                      <Text strong>关系：</Text>
                      {elder.identityName}
                    </Col>
                  </Row>
                  {index < editableReport.elderInfo.length - 1 && (
                    <div
                      style={{
                        height: "8px",
                        borderBottom: "1px solid #f0f0f0",
                        margin: "8px 0",
                      }}
                    />
                  )}
                </div>
              ))}
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Text strong>联系手机：</Text>
                  {editableReport.elderPhone}
                </Col>
                <Col span={12}>
                  <Text strong>住址：</Text>
                  {editableReport.elderAddress}
                </Col>
              </Row>
            </Card>

            {/* 随访时间 */}
            <Card title="随访时间" size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Text strong>开始时间：</Text>
                  {editableReport.startAt
                    ? dayjs(editableReport.startAt).format("YYYY-MM-DD HH:mm")
                    : "-"}
                </Col>
                <Col span={8}>
                  <Text strong>结束时间：</Text>
                  {editableReport.endAt
                    ? dayjs(editableReport.endAt).format("YYYY-MM-DD HH:mm")
                    : "-"}
                </Col>
                <Col span={8}>
                  <Text strong>时长：</Text>
                  {editableReport.durationMinutes
                    ? `${editableReport.durationMinutes}分钟`
                    : "-"}
                </Col>
              </Row>
            </Card>

            {/* 服务人员概要 */}
            <Card
              title="服务人员概要"
              size="small"
              style={{ marginBottom: 16 }}
            >
              <Row gutter={16}>
                <Col span={8}>
                  <Text strong>姓名：</Text>
                  {editableReport.staffName}
                </Col>
                <Col span={8}>
                  <Text strong>性别：</Text>
                  {editableReport.staffGender}
                </Col>
                <Col span={8}>
                  <Text strong>年龄：</Text>
                  {editableReport.staffAge}岁
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 8 }}>
                <Col span={12}>
                  <Text strong>联系手机：</Text>
                  {editableReport.staffPhone}
                </Col>
                <Col span={12}>
                  <Text strong>家庭住址：</Text>
                  {editableReport.staffAddress}
                </Col>
              </Row>
            </Card>

            {/* 总体结论 */}
            <Card title="总体结论" size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={24}>
                  <Text strong>状态：</Text>
                  <select
                    value={editableReport.overallConclusion?.status || 3}
                    onChange={(e) =>
                      handleNestedEdit(
                        "overallConclusion",
                        "status",
                        parseInt(e.target.value)
                      )
                    }
                    style={{
                      marginLeft: 8,
                      padding: "4px 8px",
                      borderRadius: "4px",
                      border: "1px solid #d9d9d9",
                      backgroundColor: "white",
                      fontWeight: "bold",
                      color:
                        (editableReport.overallConclusion?.status || 3) === 1
                          ? "#52c41a"
                          : (editableReport.overallConclusion?.status || 3) ===
                            2
                          ? "#faad14"
                          : "#f5222d",
                    }}
                  >
                    <option value={1} style={{ color: "#52c41a" }}>
                      一切安好
                    </option>
                    <option value={2} style={{ color: "#faad14" }}>
                      建议关注
                    </option>
                    <option value={3} style={{ color: "#f5222d" }}>
                      危险警告
                    </option>
                  </select>
                </Col>
              </Row>
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={24}>
                  <Text strong>结论描述：</Text>
                  <Input.TextArea
                    value={editableReport.overallConclusion?.comment || ""}
                    onChange={(e) =>
                      handleNestedEdit(
                        "overallConclusion",
                        "comment",
                        e.target.value
                      )
                    }
                    autoSize={{ minRows: 1, maxRows: 6 }}
                    placeholder="请输入总体结论描述"
                    style={{ marginTop: 8 }}
                  />
                </Col>
              </Row>
              {/* <Row gutter={16}>
                <Col span={24}>
                  <Text strong>风险（用|分隔）：</Text>
                  <Input.TextArea
                    value={
                      Array.isArray(editableReport.overallConclusion?.risks)
                        ? editableReport.overallConclusion?.risks.join("| ")
                        : editableReport.overallConclusion?.risks || ""
                    }
                    onChange={(e) => {
                      // 直接保存用户输入的原始字符串，不进行实时分割处理
                      const inputValue = e.target.value;
                      // 只有在失去焦点时才进行分割处理
                      handleNestedEdit(
                        "overallConclusion",
                        "risks",
                        inputValue
                      );
                    }}
                    onBlur={(e) => {
                      // 在失去焦点时进行分割和清理处理
                      const inputValue = e.target.value;
                      const processedRisks = inputValue
                        ? inputValue
                            .split("|")
                            .map((r) => r.trim())
                            .filter((r) => r)
                        : [];
                      handleNestedEdit(
                        "overallConclusion",
                        "risks",
                        processedRisks
                      );
                    }}
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    placeholder="请输入风险项目，多个风险用|分隔"
                    style={{ marginTop: 8 }}
                  />
                </Col>
              </Row> */}
            </Card>

            {/* AI总结 */}
            <Card title="AI总结" size="small" style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>
                  内容概要：
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: "12px",
                      color:
                        (editableReport.aiSummary?.contentSummary?.length ||
                          0) > 4000
                          ? "#f5222d"
                          : "#999",
                    }}
                  >
                    {editableReport.aiSummary?.contentSummary?.length || 0}/4000
                  </span>
                </Text>
                <Input.TextArea
                  value={editableReport.aiSummary?.contentSummary}
                  onChange={(e) =>
                    handleNestedEdit(
                      "aiSummary",
                      "contentSummary",
                      e.target.value
                    )
                  }
                  autoSize={{ minRows: 1, maxRows: 10 }}
                  style={{ marginTop: 8 }}
                  maxLength={4000}
                  showCount
                  status={
                    (editableReport.aiSummary?.contentSummary?.length || 0) >
                    4000
                      ? "error"
                      : undefined
                  }
                />
              </div>
              <div>
                <Text strong>
                  行动建议：
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: "12px",
                      color:
                        (editableReport.aiSummary?.actionAdvice?.length || 0) >
                        4000
                          ? "#f5222d"
                          : "#999",
                    }}
                  >
                    {editableReport.aiSummary?.actionAdvice?.length || 0}/4000
                  </span>
                </Text>
                <Input.TextArea
                  value={editableReport.aiSummary?.actionAdvice}
                  onChange={(e) =>
                    handleNestedEdit(
                      "aiSummary",
                      "actionAdvice",
                      e.target.value
                    )
                  }
                  autoSize={{ minRows: 1, maxRows: 10 }}
                  style={{ marginTop: 8 }}
                  maxLength={4000}
                  showCount
                  status={
                    (editableReport.aiSummary?.actionAdvice?.length || 0) > 4000
                      ? "error"
                      : undefined
                  }
                />
              </div>
            </Card>

            {/* 状态评估 */}
            <Card
              title={
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>状态评估</span>
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={handleAddConclusionRecord}
                  >
                    添加评估项目
                  </Button>
                </div>
              }
              size="small"
              style={{ marginBottom: 16 }}
            >
              {editableReport?.conclusions &&
              editableReport.conclusions.length > 0 ? (
                <div>
                  {editableReport.conclusions.map((record, index) => (
                    <Card
                      key={index}
                      size="small"
                      style={{ marginBottom: 8 }}
                      title={
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            项目 {index + 1}：
                            <AutoComplete
                              value={record.item || ""}
                              onChange={(value) =>
                                handleConclusionRecordEdit(index, "item", value)
                              }
                              options={ASSESSMENT_ITEM_OPTIONS}
                              placeholder="评估项目名称"
                              filterOption={(inputValue, option) =>
                                option!.value
                                  .toUpperCase()
                                  .indexOf(inputValue.toUpperCase()) !== -1
                              }
                              style={{
                                fontWeight: "normal",
                                marginLeft: 8,
                                width: "90%",
                              }}
                            />
                          </div>
                          <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteConclusionRecord(index)}
                            style={{ marginLeft: 8 }}
                          >
                            删除
                          </Button>
                        </div>
                      }
                    >
                      <div style={{ marginBottom: 8 }}>
                        <Text strong>状态：</Text>
                        <select
                          value={record.status || 3}
                          onChange={(e) =>
                            handleConclusionRecordEdit(
                              index,
                              "status",
                              parseInt(e.target.value)
                            )
                          }
                          style={{
                            marginLeft: 8,
                            padding: "4px 8px",
                            borderRadius: "4px",
                            border: "1px solid #d9d9d9",
                            backgroundColor: "white",
                            fontWeight: "bold",
                            color:
                              (record.status || 3) === 1
                                ? "#52c41a"
                                : (record.status || 3) === 2
                                ? "#faad14"
                                : "#f5222d",
                          }}
                        >
                          <option value={1} style={{ color: "#52c41a" }}>
                            一切安好
                          </option>
                          <option value={2} style={{ color: "#faad14" }}>
                            建议关注
                          </option>
                          <option value={3} style={{ color: "#f5222d" }}>
                            危险警告
                          </option>
                        </select>
                      </div>
                      <div
                        style={{
                          marginBottom: 8,
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "8px",
                        }}
                      >
                        <Text strong style={{ paddingTop: "6px" }}>
                          描述：
                        </Text>
                        <Input.TextArea
                          value={record.content || ""}
                          onChange={(e) =>
                            handleConclusionRecordEdit(
                              index,
                              "content",
                              e.target.value
                            )
                          }
                          autoSize={{ minRows: 1, maxRows: 4 }}
                          placeholder="评估内容描述"
                          style={{ flex: 1 }}
                        />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: "8px",
                        }}
                      >
                        <Text
                          strong
                          style={{ minWidth: "120px", paddingTop: "6px" }}
                        >
                          风险（用|分隔）：
                        </Text>
                        <Input.TextArea
                          value={
                            Array.isArray(record.risks)
                              ? record.risks.join("| ")
                              : record.risks || ""
                          }
                          onChange={(e) => {
                            // 直接保存用户输入的原始字符串，不进行实时分割处理
                            const inputValue = e.target.value;
                            handleConclusionRecordEdit(
                              index,
                              "risks",
                              inputValue
                            );
                          }}
                          onBlur={(e) => {
                            // 在失去焦点时进行分割和清理处理
                            const inputValue = e.target.value;
                            const processedRisks = inputValue
                              ? inputValue
                                  .split("|")
                                  .map((r) => r.trim())
                                  .filter((r) => r)
                              : [];
                            handleConclusionRecordEdit(
                              index,
                              "risks",
                              processedRisks
                            );
                          }}
                          autoSize={{ minRows: 1, maxRows: 1 }}
                          placeholder="风险项目，多个风险用|分隔"
                          style={{ flex: 1 }}
                        />
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    padding: "20px",
                    textAlign: "center",
                    color: "#999",
                    border: "1px dashed #d9d9d9",
                    borderRadius: "6px",
                  }}
                >
                  暂无状态评估记录
                </div>
              )}
            </Card>

            {/* 个性化需求 */}
            <Card title="个性化需求" size="small" style={{ marginBottom: 16 }}>
              <Input.TextArea
                value={editableReport.requirement}
                onChange={(e) =>
                  handleReportEdit("requirement", e.target.value)
                }
                autoSize={{ minRows: 1, maxRows: 4 }}
                placeholder="请输入个人要求"
              />
            </Card>

            {/* 反馈信息 */}
            <Card title="反馈信息" size="small">
              <div>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>反馈评论：</Text>
                  <Input.TextArea
                    value={editableReport?.feedback?.comment || ""}
                    onChange={(e) =>
                      handleNestedEdit("feedback", "comment", e.target.value)
                    }
                    autoSize={{ minRows: 1, maxRows: 6 }}
                    placeholder="请输入反馈评论"
                    style={{ marginTop: 8 }}
                  />
                </div>

                <div>
                  <Text strong>反馈附件（图片）：</Text>
                  <div style={{ marginTop: 8 }}>
                    {feedbackImages.map((imageUrl, index) => (
                      <div key={index} style={{ marginBottom: 8 }}>
                        <ImageUpload
                          value={imageUrl}
                          onChange={(url) =>
                            handleFeedbackImageChange(index, url)
                          }
                          uploadPath="feedback-attachments/"
                          accept="image/*"
                          maxCount={1}
                        />
                      </div>
                    ))}
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={handleAddFeedbackImage}
                      style={{ width: "100%", marginTop: 8 }}
                    >
                      添加反馈图片
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}
      </Modal>

      {/* 自定义样式 */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .react-resizable-handle {
            position: absolute;
            width: 10px;
            height: 100%;
            bottom: 0;
            right: -5px;
            cursor: col-resize;
            z-index: 1;
          }

          .react-resizable-handle:hover {
            border-right: 2px solid #1890ff;
          }

          .ant-table-thead > tr > th {
            position: relative;
          }

          .ant-table-thead > tr > th .react-resizable-handle {
            background: transparent;
          }

          .ant-table-thead > tr > th .react-resizable-handle:hover {
            background: rgba(24, 144, 255, 0.1);
          }
          .ant-form-item-label {
            min-width: 100px;
          }
        `,
        }}
      />
    </div>
  );
};

export default PendingReports;
